import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { ArrowLeft, Loader2, UserPlus, CheckCircle2, Copy, ExternalLink, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const IBGE_API = "https://servicodados.ibge.gov.br/api/v1/localidades";

function formatCPF(value) {
  return value
    .replace(/\D/g, "")
    .slice(0, 11)
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
}

function validateCPF(cpf) {
  const c = cpf.replace(/\D/g, "");
  if (c.length !== 11 || /^(\d)\1{10}$/.test(c)) return false;
  let s = 0;
  for (let i = 0; i < 9; i++) s += parseInt(c[i]) * (10 - i);
  let d1 = (s * 10 % 11) % 10;
  if (d1 !== parseInt(c[9])) return false;
  s = 0;
  for (let i = 0; i < 10; i++) s += parseInt(c[i]) * (11 - i);
  return (s * 10 % 11) % 10 === parseInt(c[10]);
}

function formatError(detail) {
  if (!detail) return "Ocorreu um erro. Tente novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function CadastrarGestorPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [states, setStates] = useState([]);
  const [municipalities, setMunicipalities] = useState([]);
  const [loadingMunicipalities, setLoadingMunicipalities] = useState(false);

  const [form, setForm] = useState({
    state: "", municipality: "", full_name: "", cpf: "", email: "", birth_date: ""
  });
  const [cpfError, setCpfError] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${IBGE_API}/estados?orderBy=nome`)
      .then(r => setStates(r.data))
      .catch(() => {});
  }, []);

  const setField = (f, v) => { setForm(p => ({ ...p, [f]: v })); setError(""); };

  const handleStateChange = async (uf) => {
    setField("state", uf);
    setField("municipality", "");
    setMunicipalities([]);
    if (!uf) return;
    setLoadingMunicipalities(true);
    try {
      const r = await axios.get(`${IBGE_API}/estados/${uf}/municipios?orderBy=nome`);
      setMunicipalities(r.data);
    } catch {}
    setLoadingMunicipalities(false);
  };

  const handleCPF = (v) => {
    const formatted = formatCPF(v);
    setField("cpf", formatted);
    const digits = formatted.replace(/\D/g, "");
    if (digits.length === 11) {
      setCpfError(validateCPF(formatted) ? "" : "CPF inválido");
    } else {
      setCpfError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.state || !form.municipality) { setError("Selecione o estado e município."); return; }
    if (!form.full_name.trim()) { setError("Informe o nome completo."); return; }
    if (!validateCPF(form.cpf)) { setError("CPF inválido."); return; }
    if (!form.email.trim()) { setError("Informe o email."); return; }
    if (!form.birth_date) { setError("Informe a data de nascimento."); return; }

    setLoading(true);
    setError("");
    try {
      const resp = await axios.post(
        `${API}/admin/municipal-managers`,
        {
          full_name: form.full_name.trim(),
          cpf: form.cpf,
          email: form.email.trim().toLowerCase(),
          birth_date: form.birth_date,
          state: form.state,
          municipality: form.municipality,
        },
        { withCredentials: true }
      );
      setResult(resp.data);
    } catch (e) {
      setError(formatError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(result.access_link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (result) {
    return (
      <Layout>
        <div className="max-w-xl mx-auto py-8 animate-fade-in">
          <div className="bg-white border border-border rounded-2xl p-8 text-center shadow-sm">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2" style={{ fontFamily: "Manrope" }}>
              Gestor cadastrado com sucesso!
            </h2>
            <p className="text-sm text-muted-foreground mb-1">
              <strong>{result.user.full_name}</strong>
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              {result.user.municipality} / {result.user.state}
            </p>

            {/* Email status */}
            {result.email_sent ? (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 mb-5 text-sm text-green-700">
                Email de boas-vindas enviado para <strong>{result.user.email}</strong>
              </div>
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-5 text-left">
                <div className="flex gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-700 font-medium">Email não enviado — compartilhe o link manualmente</p>
                </div>
                <p className="text-xs text-amber-600">Envie este link para o gestor definir sua senha:</p>
              </div>
            )}

            {/* Access link */}
            <div className="bg-secondary rounded-xl p-4 mb-6 text-left">
              <p className="text-xs text-muted-foreground mb-2 font-medium">Link de primeiro acesso (válido 24h):</p>
              <p className="text-xs text-foreground break-all font-mono mb-3" data-testid="access-link">
                {result.access_link}
              </p>
              <div className="flex gap-2">
                <button
                  onClick={copyLink}
                  className="flex items-center gap-1.5 px-3 py-2 text-xs bg-white border border-border rounded-lg hover:bg-secondary transition-all"
                  data-testid="copy-link-btn"
                >
                  <Copy className="w-3.5 h-3.5" />
                  {copied ? "Copiado!" : "Copiar link"}
                </button>
                <a
                  href={result.access_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-2 text-xs bg-white border border-border rounded-lg hover:bg-secondary transition-all"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Abrir link
                </a>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => { setResult(null); setForm({ state: "", municipality: "", full_name: "", cpf: "", email: "", birth_date: "" }); }}
                className="flex-1 py-3 border border-border rounded-xl text-sm font-medium hover:bg-secondary transition-all"
                data-testid="cadastrar-outro-btn"
              >
                Cadastrar outro gestor
              </button>
              <button
                onClick={() => navigate("/gestao-municipal")}
                className="flex-1 py-3 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 transition-all"
                data-testid="voltar-gestao-btn"
              >
                Voltar à gestão
              </button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-xl mx-auto py-6 animate-fade-in">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button onClick={() => navigate("/gestao-municipal")} className="p-2 rounded-lg hover:bg-secondary transition-all">
            <ArrowLeft className="w-4 h-4 text-muted-foreground" />
          </button>
          <div className="w-9 h-9 rounded-xl bg-primary flex items-center justify-center">
            <UserPlus className="w-4 h-4 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-foreground" style={{ fontFamily: "Manrope" }}>
              Cadastrar Gestor Municipal
            </h1>
            <p className="text-xs text-muted-foreground">O gestor receberá um link para definir sua senha</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="bg-white border border-border rounded-2xl p-6 space-y-5 shadow-sm">

          {/* Estado + Município */}
          <div className="space-y-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Município</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Estado *</label>
                <select
                  value={form.state}
                  onChange={e => handleStateChange(e.target.value)}
                  className="w-full h-11 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white"
                  data-testid="state-select"
                >
                  <option value="">Selecione...</option>
                  {states.map(s => (
                    <option key={s.id} value={s.sigla}>{s.sigla} — {s.nome}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Município *</label>
                <select
                  value={form.municipality}
                  onChange={e => setField("municipality", e.target.value)}
                  disabled={!form.state || loadingMunicipalities}
                  className="w-full h-11 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 bg-white disabled:opacity-50"
                  data-testid="municipality-select"
                >
                  <option value="">
                    {loadingMunicipalities ? "Carregando..." : "Selecione..."}
                  </option>
                  {municipalities.map(m => (
                    <option key={m.id} value={m.nome}>{m.nome}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <hr className="border-border" />

          {/* Dados pessoais */}
          <div className="space-y-4">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Dados do Gestor</p>

            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Nome completo *</label>
              <input
                type="text"
                value={form.full_name}
                onChange={e => setField("full_name", e.target.value)}
                placeholder="Nome completo do gestor"
                className="w-full h-11 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="full-name-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">CPF *</label>
                <input
                  type="text"
                  value={form.cpf}
                  onChange={e => handleCPF(e.target.value)}
                  placeholder="000.000.000-00"
                  inputMode="numeric"
                  className={`w-full h-11 border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${cpfError ? "border-destructive" : "border-border"}`}
                  data-testid="cpf-input"
                />
                {cpfError && <p className="text-xs text-destructive mt-1">{cpfError}</p>}
              </div>
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Data de nascimento *</label>
                <input
                  type="date"
                  value={form.birth_date}
                  onChange={e => setField("birth_date", e.target.value)}
                  max={new Date().toISOString().split("T")[0]}
                  className="w-full h-11 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  data-testid="birth-date-input"
                />
              </div>
            </div>

            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Email *</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setField("email", e.target.value)}
                placeholder="email@municipio.gov.br"
                className="w-full h-11 border border-border rounded-lg px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="manager-email-input"
              />
            </div>
          </div>

          {error && (
            <div className="flex gap-2 bg-destructive/10 border border-destructive/20 rounded-xl px-3 py-3">
              <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
              <p className="text-sm text-destructive" data-testid="form-error">{error}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !!cpfError}
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 transition-all disabled:opacity-60"
            data-testid="submit-manager-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
            {loading ? "Cadastrando..." : "Cadastrar gestor"}
          </button>
        </form>
      </div>
    </Layout>
  );
}
